import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import { DataTable, FilterBar, ConfirmDialog, UserCreateModal } from '../shared';
import { listUsers, updateUserStatus, updateUser, resendCredentials } from '../../services/userManagementService';
import SponsorFamilyModal from './SponsorFamilyModal';
import { useAuth } from '../../contexts/AuthContext';
import { tokens } from '../../theme/tokens';
import { getUserStatus, getStatusColor } from '../../utils/statusUtils';
import toast from 'react-hot-toast';
import EmailIcon from '@mui/icons-material/Email';
import Tooltip from '@mui/material/Tooltip';

/**
 * SponsorManagement Component
 * 
 * Comprehensive sponsor management interface for platform admins.
 * Features: list, search, filter, view, edit, activate/deactivate sponsors.
 * Platform-level component (sponsors are not tenant-specific).
 */
const SponsorManagement = () => {
  const { profile } = useAuth();
  
  // Table state
  const [sponsors, setSponsors] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [familyModalOpen, setFamilyModalOpen] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    organization_name: '',
  });

  // TODO: Fetch organization type options from the backend
  const orgTypes = [];

  // Fetch sponsors
  const fetchSponsors = useCallback(async () => {
    if (!profile?.tenant_id) {
      setError('No tenant context available');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Convert 0-indexed page (for Material-UI) to 1-indexed (for backend API)
      const apiPage = page + 1;
      
      const result = await listUsers({
        tenantId: profile.tenant_id,
        role: 'sponsor',
        q: searchTerm,
        status: statusFilter,
        page: apiPage,
        pageSize,
        sortBy,
        sortOrder,
      });

      if (result.error) {
        throw result.error;
      }

      setSponsors(result.data.rows);
      setTotalCount(result.data.total);
    } catch (err) {
      console.error('Error fetching sponsors:', err);
      setError('Failed to load sponsors. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [profile?.tenant_id, searchTerm, statusFilter, page, pageSize, sortBy, sortOrder]);

  // Load sponsors on mount and when dependencies change
  useEffect(() => {
    fetchSponsors();
  }, [fetchSponsors]);

  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'N/A',
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
    },
    {
      key: 'organization_name',
      header: 'Organization',
      width: 180,
      render: (row) => row.organization_name || 'Individual',
    },
    {
      key: 'phone',
      header: 'Phone',
      width: 150,
      render: (row) => row.phone || 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      width: 120,
      render: (row) => {
        const displayStatus = getUserStatus(row.status);
        return (
          <Chip
            label={displayStatus}
            size="small"
            color={getStatusColor(displayStatus)}
          />
        );
      },
    },
    {
      key: 'created_at',
      header: 'Registered',
      sortable: true,
      width: 150,
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
    },
  ];

  // Action handlers
  const handleView = (sponsor) => {
    setSelectedSponsor(sponsor);
    setViewDialogOpen(true);
  };

  const handleViewFamily = (sponsor) => {
    setSelectedSponsor(sponsor);
    setFamilyModalOpen(true);
  };

  const handleEdit = (sponsor) => {
    setSelectedSponsor(sponsor);
    setEditForm({
      first_name: sponsor.first_name || '',
      last_name: sponsor.last_name || '',
      email: sponsor.email || '',
      phone: sponsor.phone || '',
      organization_name: sponsor.organization_name || '',
    });
    setEditDialogOpen(true);
  };

  const handleDeactivate = (sponsor) => {
    setSelectedSponsor(sponsor);
    setConfirmAction('deactivate');
    setConfirmDialogOpen(true);
  };

  const handleActivate = (sponsor) => {
    setSelectedSponsor(sponsor);
    setConfirmAction('activate');
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedSponsor || !profile?.tenant_id) return;

    const newStatus = confirmAction === 'activate' ? 'active' : 'inactive';
    
    const result = await updateUserStatus(
      selectedSponsor.id,
      profile.tenant_id,
      newStatus
    );

    if (result.error) {
      toast.error(`Failed to ${confirmAction} sponsor`);
    } else {
      await fetchSponsors();
    }

    setConfirmDialogOpen(false);
    setSelectedSponsor(null);
    setConfirmAction(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedSponsor || !profile?.tenant_id) return;

    setLoading(true);
    try {
      const result = await updateUser(
        selectedSponsor.id,
        profile.tenant_id,
        editForm
      );

      if (result.error) {
        throw result.error;
      }

      await fetchSponsors();
      setEditDialogOpen(false);
      setSelectedSponsor(null);
    } catch (err) {
      console.error('Error updating sponsor:', err);
      toast.error('Failed to update sponsor');
    } finally {
      setLoading(false);
    }
  };

  // Handle resend credentials
  const handleResendCredentials = async (userId) => {
    setResendingEmail(userId);
    try {
      const result = await resendCredentials(userId);
      if (result.error) {
        throw result.error;
      }
      // Show password in toast if email might have failed
      if (result.data?.data?.password) {
        toast.success(
          `Credentials email sent! Password: ${result.data.data.password}\n\n⚠️ If email fails, share this password manually.`,
          { duration: 10000 }
        );
      } else {
        toast.success('Credentials email sent successfully!');
      }
    } catch (error) {
      console.error('Error resending credentials:', error);
      toast.error('Failed to resend credentials email');
    } finally {
      setResendingEmail(null);
    }
  };

  // Actions slot for table
  const actionsSlot = (row) => (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
      <Tooltip title="Resend credentials email">
        <IconButton
          size="small"
          onClick={() => handleResendCredentials(row.id)}
          disabled={resendingEmail === row.id}
          color="primary"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.primary}`,
              outlineOffset: 2,
            },
          }}
        >
          <EmailIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        onClick={() => handleViewFamily(row)}
        aria-label={`View family for ${row.first_name} ${row.last_name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <FamilyRestroomIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleView(row)}
        aria-label={`View ${row.first_name} ${row.last_name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleEdit(row)}
        aria-label={`Edit ${row.first_name} ${row.last_name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      {row.status === 'active' ? (
        <IconButton
          size="small"
          onClick={() => handleDeactivate(row)}
          aria-label={`Deactivate ${row.first_name} ${row.last_name}`}
          color="error"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.danger}`,
              outlineOffset: 2,
            },
          }}
        >
          <PersonOffIcon fontSize="small" />
        </IconButton>
      ) : (
        <IconButton
          size="small"
          onClick={() => handleActivate(row)}
          aria-label={`Activate ${row.first_name} ${row.last_name}`}
          color="success"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.success}`,
              outlineOffset: 2,
            },
          }}
        >
          <PersonIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  // Clear filters handler
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setPage(0);
  };

  return (
    <Box sx={{ py: 0, px: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25, fontSize: '1.125rem' }}>
            Sponsor Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Manage US-based sponsors supporting international patients
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.primary}`,
              outlineOffset: 2,
            },
          }}
        >
          Add Sponsor
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Bar */}
      <FilterBar
        value={searchTerm}
        onChange={setSearchTerm}
        onClear={handleClearFilters}
        placeholder="Search by name, email, or organization..."
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            label="Status"
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(0);
            }}
            label="Type"
          >
            <MenuItem value="all">All Types</MenuItem>
            {orgTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FilterBar>

      {/* Stats */}
      <Box sx={{ mb: 1, mt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {totalCount} total sponsor{totalCount !== 1 ? 's' : ''}
          {statusFilter !== 'all' && ` (filtered by ${statusFilter})`}
        </Typography>
      </Box>

      {/* Data Table */}
      <DataTable
        columns={columns}
        rows={sponsors}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        sortBy={sortBy}
        sortDir={sortOrder}
        loading={loading}
        error={error}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        onSortChange={(key, dir) => {
          setSortBy(key);
          setSortOrder(dir);
        }}
        getRowId={(row) => row.id}
        actionsSlot={actionsSlot}
        emptyMessage="No sponsors found. Try adjusting your filters or add a new sponsor."
        ariaLabel="sponsors table"
      />

      {/* Family Modal */}
      {selectedSponsor && (
        <SponsorFamilyModal
          open={familyModalOpen}
          onClose={() => setFamilyModalOpen(false)}
          sponsor={selectedSponsor}
        />
      )}

      {/* Create User Modal */}
      <UserCreateModal
        open={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onUserCreated={fetchSponsors}
        role="sponsor"
      />

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sponsor Details</DialogTitle>
        <DialogContent>
          {selectedSponsor && (
            <Box sx={{ pt: tokens.space.x2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedSponsor.first_name} {selectedSponsor.last_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip label={selectedSponsor.status} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedSponsor.email}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedSponsor.phone || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Organization</Typography>
                  <Typography variant="body1">{selectedSponsor.organization_name || 'Individual'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Registered</Typography>
                  <Typography variant="body1">
                    {selectedSponsor.created_at ? new Date(selectedSponsor.created_at).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Sponsor</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: tokens.space.x2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organization Name"
                  value={editForm.organization_name}
                  onChange={(e) => setEditForm({ ...editForm, organization_name: e.target.value })}
                  helperText="Leave blank for individual sponsors"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title={confirmAction === 'activate' ? 'Activate Sponsor' : 'Deactivate Sponsor'}
        message={
          selectedSponsor
            ? `Are you sure you want to ${confirmAction} ${selectedSponsor.first_name} ${selectedSponsor.last_name}?`
            : ''
        }
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setSelectedSponsor(null);
          setConfirmAction(null);
        }}
        confirmText={confirmAction === 'activate' ? 'Activate' : 'Deactivate'}
        severity={confirmAction === 'deactivate' ? 'warning' : 'info'}
      />
    </Box>
  );
};

export default SponsorManagement;
